import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './Spinner.css';

/**
 * Reusable spinner component with customizable size, color, and text options
 * @param {Object} props - Component props
 * @param {string} props.size - Size of the spinner: 'small', 'medium', or 'large'
 * @param {string} props.color - Custom color (CSS color value)
 * @param {boolean} props.showText - Whether to show loading text
 * @param {string} props.customText - Custom loading text (if not using i18n)
 * @param {boolean} props.fullPage - Whether spinner should take full page with overlay
 * @param {number} props.thickness - Thickness of the spinner line (1-5)
 * @returns {React.Component} Spinner component
 */
const Spinner = ({
  size = 'medium',
  color,
  showText = true,
  customText,
  fullPage = false,
  thickness = 3
}) => {
  const { t } = useTranslation('common');
  
  // Map size to pixel values
  const sizeMap = {
    small: '20px',
    medium: '40px',
    large: '60px'
  };

  // Set spinner size
  const spinnerSize = sizeMap[size] || sizeMap.medium;
  
  // Set spinner style
  const spinnerStyle = {
    width: spinnerSize,
    height: spinnerSize,
    borderWidth: `${thickness}px`,
    // Use design system tokens for default colors
    borderColor: color ? `${color}40` : 'var(--color-border-light)',
    borderTopColor: color || 'var(--color-primary)'
  };

  // Component to render
  const spinnerComponent = (
    <div className={`spinner-container ${fullPage ? 'full-page' : ''}`}>
      <div className="spinner" style={spinnerStyle}></div>
      {showText && (
        <p className="spinner-text" style={{ color: color || 'var(--color-text-primary)' }}>
          {customText || t('app.loading')}
        </p>
      )}
    </div>
  );

  // If fullPage is true, render with overlay
  if (fullPage) {
    return (
      <div className="spinner-overlay">
        {spinnerComponent}
      </div>
    );
  }

  return spinnerComponent;
};

Spinner.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.string,
  showText: PropTypes.bool,
  customText: PropTypes.string,
  fullPage: PropTypes.bool,
  thickness: PropTypes.number
};

export default Spinner;